import { baseQuery } from "./base-query";
import { createApi } from "@reduxjs/toolkit/query/react";
import { clientApi } from "@/services/client/client-api";
import { authApi } from "@/services/auth/auth-api";
import { spOwnerPartnerApi } from "@/services/spowner/partner-api";
import { tenantManagementApi } from "@/services/tenant/tenant-api";
import { userManagementApi } from "@/services/user-management/user-managment-api";

export const pageAccessApi = createApi({
  reducerPath: "pageAccessApi",
  baseQuery,
  tagTypes: ["TenantPages"],
  endpoints: (builder) => ({
    getTenantAllowedPages: builder.query<{ pages: string[] }, { tenantId?: string } | void>({
      query: (variables) => ({
        document: `
          query GetTenantAllowedPages($tenantId: ID) {
            getTenantAllowedPages(tenantId: $tenantId) { pages }
          }
        `,
        variables,
      }),
      transformResponse: (response: { getTenantAllowedPages: { pages: string[] } }) =>
        response.getTenantAllowedPages,
      providesTags: ["TenantPages"],
    }),
    setTenantAllowedPages: builder.mutation<{ success: boolean }, { tenantId: string; pages: string[] }>({
      query: (variables) => ({
        document: `
          mutation SetTenantAllowedPages($tenantId: ID!, $pages: [PageKeyEnum!]!) {
            setTenantAllowedPages(tenantId: $tenantId, pages: $pages) { success }
          }
        `,
        variables,
      }),
      transformResponse: (response: { setTenantAllowedPages: { success: boolean } }) =>
        response.setTenantAllowedPages,
      invalidatesTags: ["TenantPages"],
      async onQueryStarted(_vars, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh tenants and any client/partner listings depending on templates
          dispatch(tenantManagementApi.util.invalidateTags(["tenantManagement"]));
          dispatch(clientApi.util.invalidateTags(["Clients"]));
          dispatch(spOwnerPartnerApi.util.invalidateTags(["spOwnerPartners"]));
        } catch {}
      },
    }),
    setUserAllowedPages: builder.mutation<{ success: boolean }, { userId: string; pages: string[] }>({
      query: (variables) => ({
        document: `
          mutation SetUserAllowedPages($userId: ID!, $pages: [PageKeyEnum!]!) {
            setUserAllowedPages(userId: $userId, pages: $pages) { success }
          }
        `,
        variables,
      }),
      transformResponse: (response: { setUserAllowedPages: { success: boolean } }) =>
        response.setUserAllowedPages,
      async onQueryStarted(_vars, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh clients list and user-management listings
          dispatch(clientApi.util.invalidateTags(["Clients"]));
          dispatch(userManagementApi.util.invalidateTags(["userManagement"]));
          // Refresh current user so sidebar/route guards pick up updated allowedPages
          dispatch(authApi.util.invalidateTags(["CurrentUser"]));
        } catch {}
      },
    }),
    setPartnerAccessibleClients: builder.mutation<
      { success: boolean },
      { partnerId: string; clientIds: string[] }
    >({
      query: (variables) => ({
        document: `
          mutation SetPartnerAccessibleClients($partnerId: ID!, $clientIds: [ID!]!) {
            setPartnerAccessibleClients(partnerId: $partnerId, clientIds: $clientIds) { success }
          }
        `,
        variables,
      }),
      transformResponse: (response: { setPartnerAccessibleClients: { success: boolean } }) =>
        response.setPartnerAccessibleClients,
      async onQueryStarted(_vars, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Refresh partners (totals) and clients (filters, pickers)
          dispatch(spOwnerPartnerApi.util.invalidateTags(["spOwnerPartners"]));
          dispatch(clientApi.util.invalidateTags(["Clients"]));
        } catch {}
      },
    }),
  }),
});

export const {
  useGetTenantAllowedPagesQuery,
  useSetTenantAllowedPagesMutation,
  useSetUserAllowedPagesMutation,
  useSetPartnerAccessibleClientsMutation,
} = pageAccessApi;


